import React from "react";
import {
  Box,
  Button,
  HStack,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FaTable } from "react-icons/fa";
import { Panel, DataTable, MiniIconButton, EmptyState, StatusBadge } from "../ui";
import { getPositionStatusLabelKey, humanizeLabel, parseDate } from "./dashboardUtils";

const CellText = ({ value }) => (
  <Text fontSize="12px" color="#243543" noOfLines={1} sx={{ marginBottom: "0 !important" }}>
    {value || "-"}
  </Text>
);

// Status colour schemes for the positioning detail table, mirroring the
// vocabulary in constants/statuses.js (signed=green, awaiting=orange,
// positioned=blue) so the dot matches the rest of the portal.
const POSITION_STATUS_COLORS = {
  signed: "green",
  positioned: "blue",
  awaitingClientFeedback: "orange",
  fallback: "gray",
};

const filterRowsByWeek = (rows, weekRange) => {
  if (!weekRange?.fromDate || !weekRange?.toDate) return rows;
  const from = parseDate(weekRange.fromDate);
  const to = parseDate(weekRange.toDate);
  if (!from || !to) return rows;
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return rows.filter((row) => {
    const created = parseDate(row.createdAt);
    if (!created) return false;
    const createdDay = new Date(created.getFullYear(), created.getMonth(), created.getDate());
    return createdDay >= fromDay && createdDay <= toDay;
  });
};

const PilotDetailTableCard = ({ t, rows, isLoading, weekRange, weekOffset, onPrevWeek, onNextWeek, onCurrentWeek, onClearWeek }) => {
  const visibleRows = weekOffset !== null ? filterRowsByWeek(rows, weekRange) : rows;

  const columns = [
    { key: "consultant", label: t("dashboard.detailTable.columns.consultant"), width: "18%" },
    { key: "client", label: t("dashboard.detailTable.columns.client"), width: "18%" },
    { key: "jobTitle", label: t("dashboard.detailTable.columns.jobTitle"), width: "18%" },
    { key: "status", label: t("dashboard.detailTable.columns.status"), width: "17%" },
    { key: "sales", label: t("dashboard.detailTable.columns.sales"), width: "14%" },
    { key: "recruiter", label: t("dashboard.detailTable.columns.recruiter"), width: "15%" },
  ];

  return (
    <Panel
      title={t("dashboard.detailTable.title")}
      filters={
        <HStack spacing="8px" flexWrap="wrap">
          <MiniIconButton
            aria-label={t("dashboard.detailTable.prevWeek")}
            icon={<ChevronLeftIcon />}
            onClick={onPrevWeek}
          />
          <Text
            fontSize="12px"
            fontWeight="700"
            color="portal.text"
            minW="180px"
            textAlign="center"
            sx={{ marginBottom: "0 !important" }}
          >
            {weekOffset !== null ? weekRange?.label : t("dashboard.detailTable.allMatches")}
          </Text>
          <MiniIconButton
            aria-label={t("dashboard.detailTable.nextWeek")}
            icon={<ChevronRightIcon />}
            onClick={onNextWeek}
          />
          {weekOffset !== null && weekOffset !== 0 && (
            <Button variant="secondary" size="sm" h="28px" px="10px" fontSize="11px" onClick={onCurrentWeek}>
              {t("dashboard.detailTable.thisWeek")}
            </Button>
          )}
          {weekOffset !== null && (
            <Button variant="secondary" size="sm" h="28px" px="10px" fontSize="11px" onClick={onClearWeek}>
              {t("dashboard.detailTable.showAll")}
            </Button>
          )}
        </HStack>
      }
    >
      <Box maxH="360px" overflowY="auto">
        <DataTable
          columns={columns}
          rows={visibleRows}
          loading={isLoading}
          emptyState={
            <EmptyState
              icon={<FaTable />}
              title={t("dashboard.detailTable.empty")}
            />
          }
          renderRow={(row) => (
            <Tr key={row.id}>
              <Td><CellText value={row.consultantName} /></Td>
              <Td><CellText value={row.clientName} /></Td>
              <Td><CellText value={row.jobTitle} /></Td>
              <Td>
                {(() => {
                  const statusKey = getPositionStatusLabelKey(row.rawStatus);
                  const label =
                    statusKey === "fallback"
                      ? humanizeLabel(row.rawStatus)
                      : t(`dashboard.detailTable.statuses.${statusKey}`);
                  if (!label) return <CellText value="-" />;
                  return <StatusBadge label={label} colorScheme={POSITION_STATUS_COLORS[statusKey] || "gray"} />;
                })()}
              </Td>
              <Td><CellText value={row.salesName} /></Td>
              <Td><CellText value={row.recruiterName} /></Td>
            </Tr>
          )}
        />
      </Box>
    </Panel>
  );
};

export default PilotDetailTableCard;
